import React, { useEffect, useState } from "react";
import { usePrevious } from "react-use";
import { Alert, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { Geometry } from "geojson";
import { noop } from "lodash";

import Upload from "fond/project/panels/upload/Upload";
import { addImport } from "fond/redux/imports";
import { isImportInProgress } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

interface LayerUploadProps {
  layerId: string;
  displayName: string;
  title: string;
  geometryType: Geometry["type"];
  onComplete?: () => void;
  hasExistingData?: boolean;
}

const LayerUpload: React.FC<LayerUploadProps> = ({ layerId, displayName, title, geometryType, onComplete, hasExistingData = false }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const dispatch = useAppDispatch();
  const { goBack } = useStackedNavigationContext();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const importState = useAppSelector((state) => state.imports[versionId]?.[layerId]);
  const uploading = isImportInProgress(importState?.status);
  const prevState = usePrevious(importState?.status);

  useEffect(() => {
    if (prevState !== "COMPLETE" && importState?.status === "COMPLETE") {
      onComplete?.();
    }
  }, [importState?.status, prevState, onComplete]);

  const proceedWithUpload = () => {
    dispatch(
      addImport({
        versionId,
        layerKey: layerId,
        displayName,
        files: selectedFiles,
      })
    );

    setSelectedFiles([]);
    setShowConfirmModal(false);
  };

  const handleOnUpload = () => {
    if (hasExistingData) {
      setShowConfirmModal(true);
    } else {
      proceedWithUpload();
    }
  };

  const handleBack = () => {
    if (!uploading) {
      goBack();
    }
  };

  const handleConfirmReplace = () => {
    proceedWithUpload();
  };

  const handleCancelReplace = () => {
    setShowConfirmModal(false);
  };

  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={handleBack} />
      <ConfirmModal
        open={showConfirmModal}
        header="Replace existing data?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">This will replace all existing {displayName.toLowerCase()} data. This action cannot be undone.</Typography>
        }
        onConfirm={handleConfirmReplace}
        onCancel={handleCancelReplace}
      />
      <Typography fontWeight="500">{displayName}</Typography>
      {uploading && (
        <>
          <Alert severity="info" sx={{ my: 1 }}>
            FOND is now processing your data, and it will be visible on the map shortly.
          </Alert>
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
          </Paper>
        </>
      )}
      {!uploading && (
        <>
          <Upload
            layer={{
              id: layerId,
              geometryType,
              name: displayName,
            }}
            onSelectFiles={(validFiles) => {
              setSelectedFiles(validFiles);
            }}
            autoUpload={false}
            onBeginUpload={noop}
            onRemove={noop}
            includeLayerName={false}
            convertToDualSidedField={null}
          />
          <Box>
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
              <Button color="primary" size="small" onClick={handleBack}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={selectedFiles.length === 0}
                onClick={handleOnUpload}
                sx={{ ml: 1, px: 2 }}
                data-testid="upload-button"
              >
                Upload
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LayerUpload;
